// Frontend/src/api/hoaAdmins.ts
import api from "./client";

export type ApprovalStatus = "pending_approval" | "approved" | "rejected";

export type HoaAdminUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  organization_name: string | null;
  organization_id: string | null;
  service_charge_rate: string; // decimal string from backend
  max_units_allowed: number;
  subscription_status: "active" | "suspended" | "expired";
  approval_status: ApprovalStatus;
  current_unit_count: number;
  can_add_units: boolean;
  date_joined: string;
};

export type CreateHoaAdminData = {
  email: string;
  first_name: string;
  last_name: string;
  organization_name?: string;
  max_units_allowed?: number;
};

export type UpdateHoaAdminData = {
  service_charge_rate?: number;
  max_units_allowed?: number;
  subscription_status?: "active" | "suspended" | "expired";
  organization_name?: string;
};

export async function listHoaAdmins(): Promise<HoaAdminUser[]> {
  const { data } = await api.get<HoaAdminUser[]>("/auth/hoa-admins/");
  return data;
}

export async function createHoaAdmin(payload: CreateHoaAdminData): Promise<HoaAdminUser> {
  const { data } = await api.post<HoaAdminUser>("/auth/hoa-admins/", payload);
  return data;
}

export async function updateHoaAdmin(
  id: string,
  payload: UpdateHoaAdminData
): Promise<HoaAdminUser> {
  const { data } = await api.patch<HoaAdminUser>(`/auth/hoa-admins/${id}/`, payload);
  return data;
}

export async function approveHoaAdmin(id: string): Promise<HoaAdminUser> {
  const { data } = await api.post<HoaAdminUser>(`/auth/hoa-admins/${id}/approve/`);
  return data;
}

export async function rejectHoaAdmin(
  id: string,
  reason?: string
): Promise<HoaAdminUser> {
  const { data } = await api.post<HoaAdminUser>(`/auth/hoa-admins/${id}/reject/`, {
    reason: reason ?? "",
  });
  return data;
}

export async function suspendHoaAdmin(
  id: string,
  reason?: string
): Promise<HoaAdminUser> {
  const { data } = await api.post<HoaAdminUser>(`/auth/hoa-admins/${id}/suspend/`, {
    reason: reason ?? "",
  });
  return data;
}

export async function reactivateHoaAdmin(id: string): Promise<HoaAdminUser> {
  const { data } = await api.post<HoaAdminUser>(`/auth/hoa-admins/${id}/reactivate/`);
  return data;
}

export async function resendHoaAdminCredentials(id: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(`/auth/hoa-admins/${id}/resend_credentials/`);
  return data;
}

/** Send a reminder to the HOA admin to configure their payment method. */
export async function sendSetupReminder(id: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(
    `/auth/hoa-admins/${id}/send_setup_reminder/`
  );
  return data;
}
